/**
 * https://developer.openstack.org/api-ref/data-protection-orchestration/
 */

import * as R from 'ramda';
import {
    Checkpoint,
    Operation,
    Plan,
    Trigger,
} from 'services/backups/modules/BackupModule/BackupModule.types';
import { OpenStackApiHelper } from 'utils/OpenStackApiHelper/OpenStackApiHelper';

const BASE_URL = '/infra/backup/v1/%projectId%';
export const NOVA_PROVIDER_ID = '37997f75-0637-4a69-bf7e-49ff2ff11fa5';
export const TROVE_PROVIDE_ID = '7ab9410c-edda-4623-a51a-1bb806666cb3';

type Service = 'TROVE' | 'NOVA';

export enum BackupType {
    disabled = 'Отключено',
    full = 'Полное',
    incremental = 'Инкрементальное',
}

type RestoreServerDestination = 'new' | 'existing';

type RestoreServer = {
    restore: {
        restore_target: null;
        provider_id: string;
        restore_auth: null;
        parameters: {
            [key: string]: {
                restore_net_id: string;
                destination: RestoreServerDestination;
                restore_name: string;
                restore_keypair?: string;
            };
        };
        checkpoint_id: string;
    };
};

const _getProviderId = (service: Service = 'NOVA') => {
    switch (true) {
        case service === 'TROVE':
            return TROVE_PROVIDE_ID;
        case service === 'NOVA':
        default:
            return NOVA_PROVIDER_ID;
    }
};

const getCheckpointsUrl = (service?: Service) =>
    `${BASE_URL}/providers/${_getProviderId(service)}/checkpoints`; // checkpoints used only for servers

export const BackupsApi = {
    async loadPlan(id) {
        const { plan } = await OpenStackApiHelper.openstackRpcCall({
            url: `${BASE_URL}/plans/${id}`,
        });

        return plan as Plan;
    },

    async loadPlansList({ scheduled }: { scheduled?: boolean } = {}) {
        const { plans } = await OpenStackApiHelper.openstackRpcCall({
            url: `${BASE_URL}/plans`,
            data: {
                scheduled: scheduled != null ? +scheduled : undefined,
            },
        });

        return plans as Plan[];
    },

    async loadTriggersList() {
        const { triggers } = await OpenStackApiHelper.openstackRpcCall({
            url: `${BASE_URL}/triggers`,
        });

        return triggers as Trigger[];
    },

    async loadScheduledOperationsList() {
        const { operations } = await OpenStackApiHelper.openstackRpcCall({
            url: `${BASE_URL}/scheduled_operations`,
        });

        return operations as Operation[];
    },

    async setPlanStatus(id, status) {
        await OpenStackApiHelper.openstackRpcCall({
            url: `${BASE_URL}/plans/${id}`,
            method: 'PUT',
            data: {
                plan: {
                    status,
                },
            },
        });
    },

    async createPlan({ name, resources, parameters = {}, service }) {
        const { plan } = await OpenStackApiHelper.openstackRpcCall({
            url: `${BASE_URL}/plans`,
            method: 'POST',
            data: {
                plan: {
                    name,
                    resources,
                    parameters,
                    provider_id: _getProviderId(service),
                },
            },
        });

        return plan as Plan;
    },

    async updatePlan({ id, name, resources }) {
        const { plan } = await OpenStackApiHelper.openstackRpcCall({
            url: `${BASE_URL}/plans/${id}`,
            method: 'PUT',
            data: {
                plan: {
                    name,
                    resources,
                },
            },
        });

        return plan as Plan;
    },

    async deletePlan(id, withCheckpoints = true) {
        await OpenStackApiHelper.openstackRpcCall({
            url: `${BASE_URL}/plans/${id}?with_checkpoints=${withCheckpoints}`,
            method: 'DELETE',
        });
    },

    async createTrigger({ name, pattern }) {
        const { trigger_info } = await OpenStackApiHelper.openstackRpcCall({
            url: `${BASE_URL}/triggers`,
            method: 'POST',
            data: {
                trigger_info: {
                    name,
                    type: 'time',
                    properties: {
                        format: 'crontab',
                        pattern,
                    },
                },
            },
        });

        return trigger_info as Trigger;
    },

    async deleteTrigger(id) {
        await OpenStackApiHelper.openstackRpcCall({
            url: `${BASE_URL}/triggers/${id}`,
            method: 'DELETE',
        });
    },

    async createScheduledOperation({
        name,
        plan_id,
        trigger_id,
        max_backups,
        operation_type,
        retention_duration,
        backup_mode,
        service,
    }) {
        const {
            scheduled_operation,
        } = await OpenStackApiHelper.openstackRpcCall({
            url: `${BASE_URL}/scheduled_operations`,
            method: 'POST',
            data: {
                scheduled_operation: {
                    name,
                    operation_type,
                    trigger_id,
                    operation_definition: {
                        provider_id: _getProviderId(service),
                        plan_id,
                        max_backups,
                        retention_duration,
                        backup_mode,
                    },
                },
            },
        });

        return scheduled_operation as Operation;
    },

    async deleteScheduledOperation(id) {
        await OpenStackApiHelper.openstackRpcCall({
            url: `${BASE_URL}/scheduled_operations/${id}`,
            method: 'DELETE',
        });
    },

    async enableScheduledOperation(id) {
        await OpenStackApiHelper.openstackRpcCall({
            url: `${BASE_URL}/scheduled_operations/${id}/enable`,
            method: 'PUT',
        });
    },

    async disableScheduledOperation(id) {
        await OpenStackApiHelper.openstackRpcCall({
            url: `${BASE_URL}/scheduled_operations/${id}/disable`,
            method: 'PUT',
        });
    },

    loadCheckpoints: (service) => async () => {
        const { checkpoints } = await OpenStackApiHelper.openstackRpcCall({
            url: getCheckpointsUrl(service),
        });

        return fixCheckpoints(checkpoints) as Checkpoint[];
    },

    removeCheckpoint: R.curry(async (service: Service, id) => {
        await OpenStackApiHelper.openstackRpcCall({
            url: `${getCheckpointsUrl(service)}/${id}`,
            method: 'DELETE',
        });
    }),

    createCheckpoint: R.curry(
        async (service: Service, { plan_id, extra_info }) => {
            const { checkpoint } = await OpenStackApiHelper.openstackRpcCall({
                url: getCheckpointsUrl(service),
                method: 'POST',
                data: {
                    checkpoint: {
                        plan_id,
                        extra_info,
                    },
                },
            });

            return _fixCheckpoint(checkpoint) as Checkpoint;
        },
    ),

    async loadCheckpoint(checkpointId) {
        const { checkpoint } = await OpenStackApiHelper.openstackRpcCall({
            url: `${getCheckpointsUrl()}/${checkpointId}`,
        });

        return _fixCheckpoint(checkpoint);
    },

    loadCheckpointResourceMetadata: async ({ checkpointId, resourceId }) => {
        const { resource_metadata } = await OpenStackApiHelper.openstackRpcCall(
            {
                url: `${getCheckpointsUrl()}/${checkpointId}/resource_metadata/${resourceId}`,
            },
        );
        return resource_metadata;
    },

    async loadOperationLogs(status) {
        const { operation_logs } = await OpenStackApiHelper.openstackRpcCall({
            url: `${BASE_URL}/operation_logs`,
            data: {
                status,
            },
        });

        return operation_logs;
    },

    async restoreServer({
        restoreExistingServer,
        checkpointId,
        serverId,
        networkId,
        serverName,
        keyName,
    }) {
        const osNovaServer = `OS::Nova::Server#${serverId}`;

        const data: RestoreServer = {
            restore: {
                restore_target: null,
                provider_id: NOVA_PROVIDER_ID,
                restore_auth: null,
                parameters: {
                    [osNovaServer]: {
                        restore_net_id: networkId,
                        destination: restoreExistingServer ? 'existing' : 'new',
                        restore_name: serverName,
                    },
                },
                checkpoint_id: checkpointId,
            },
        };

        if (keyName) {
            data.restore.parameters[osNovaServer].restore_keypair = keyName;
        }

        const { restore } = await OpenStackApiHelper.openstackRpcCall({
            url: `${BASE_URL}/restores`,
            method: 'POST',
            data,
        });

        return restore;
    },

    async loadRestore(id) {
        const { restore } = await OpenStackApiHelper.openstackRpcCall({
            url: `${BASE_URL}/restores/${id}`,
        });

        return restore;
    },
};

const _safeParse = (i) => {
    try {
        return JSON.parse(i);
    } catch (error) {
        return i;
    }
};

const _fixCheckpoint = R.evolve({
    extra_info: _safeParse,
});

function fixCheckpoints(checkpoints: Checkpoint[]) {
    return checkpoints.map((checkpoint) => {
        checkpoint.incrementalCheckpoints = [];
        return _fixCheckpoint(checkpoint);
    });
}
